import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  useCaseDetail,
  useUpdateCaseStatus,
} from "../../../hooks/useWorkshopQueries";
import { CaseProgressChecklist } from "../../../Component/Workshop/CaseDetail/CaseProgressChecklist";
import {
  QuickStat,
  OverviewTab,
  DocumentsTab,
  SurveysTab,
  InsuranceTab,
  OnSpotServicesTab,
  TimelineTab,
  DailyUpdateTab,
} from "../../../Component/Workshop/CaseDetail/CaseTabs";
import { WorkshopTab } from "../../../Component/Workshop/CaseDetail/Workshop/WorkshopTab";
import {
  Badge,
  SLABadge,
  PriorityBadge,
  Avatar,
  Spinner,
  ErrorState,
  StageBar,
  Modal,
  STAGE_LABELS,
  STATUS_LABELS,
  stageBadge,
  statusBadge,
  formatDate,
  formatINR,
  sourceMeta,
} from "../../../Component/Workshop/UI/index";
import { BackIcon, EditIcon } from "../../../Component/Workshop/UI/icons";
import { CaseOverviewSnapshot } from "./CaseOverviewSnapshot";

const DETAIL_TABS = [
  "Overview",
  "On-Spot Services",
  "Documents",
  "Insurance",
  "Surveys",
  "Workshop",
  "Timeline",
  "Daily Updates",
];
const STAGE_ORDER = [
  "CASE_CREATED",
  "SPOT SURVEY",
  "WORKSHOP_REPAIR",
  "SURVEY_PROCESS",
  "DELIVERY_PAYMENT",
  "CLAIM_SETTLEMENT",
];
const stageIndex = (stage) => STAGE_ORDER.indexOf(stage);
const STATUS_ORDER = [
  "CASE_REGISTERED",
  "CUSTOMER_REGISTERED",
  "DOCUMENTS_PENDING",
  "DOCUMENTS_SUBMITTED",
  "INTIMATION_RAISED",
  "SPOT_SURVEY_ASSIGNED",
  "SPOT_SURVEY_COMPLETED",
  "WORKSHOP_ASSIGNED",
  "VEHICLE_PICKED",
  "VEHICLE_RECEIVED_AT_WORKSHOP",
  "WORKSHOP_ESTIMATE",
  "ESTIMATE_APPROVED",
  "CLAIM_FORM_SENT_TO_CUSTOMER",
  "CLAIM_FORM_SIGNED",
  "CLAIM_FORM_APPROVED",
  "CLAIM_SUBMITTED",
  "WORKSHOP_SURVEY_ASSIGNED",
  "WORKSHOP_SURVEY_COMPLETED",
  "DISMANTLING_APPROVED",
  "DISMANTLING_COMPLETED",
  "SECOND_SURVEY_ASSIGNED",
  "SECOND_SURVEY_COMPLETED",
  "REPAIR_APPROVED",
  "REPAIR_STARTED",
  "WORK_STARTED",
  "PART_ORDERED",
  "PART_DELIVERED",
  "REPAIR_COMPLETED",
  "VEHICLE_FINALIZED",
  "TRUCK_FINALIZED",
  "ROAD_TRIAL_COMPLETED",
  "READY_FOR_INSPECTION",
  "READY_FOR_REINSPECTION",
  "REINSPECTION_COMPLETED",
  "PROFORMA_UPLOADED",
  "PROFORMA_RECEIVED",
  "ASSESSMENT_RECEIVED",
  "ASSESSMENT_CROSSCHECKED",
  "TAX_INVOICE_RECEIVED",
  "PAYMENT_REQUEST_SENT",
  "PAYMENT_COMPLETED",
  "VEHICLE_DELIVERED",
  "CLAIM_SETTLED",
  "CASE_CLOSED",
];

export default function CaseDetailPage() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [tab, setTab] = useState(location.state?.activeTab ?? "Overview"); // ← change default
  const [returnListTab] = useState(location.state?.listTab ?? "ALL");
  const [statusModal, setStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [statusError, setStatusError] = useState("");
  const [showOverview, setShowOverview] = useState(false);

  // ── Real query ─────────────────────────────────────────────────────────────
  const { data: detail, isLoading, isError, refetch } = useCaseDetail(caseId);
  const updateStatus = useUpdateCaseStatus();

  // clear the location state so refresh doesn't re-apply it
  useEffect(() => {
    if (location.state?.activeTab) {
      navigate(".", { replace: true, state: {} });
    }
  }, []);

  const handleStatusUpdate = async () => {
    setStatusError("");
    try {
      await updateStatus.mutateAsync({
        caseId,
        status: newStatus,
        description: statusNote || "Status updated by admin",
        role: "ADMIN",
      });
      refetch();
    } catch (err) {
      console.error("Status update failed:", err);
      setStatusError(
        err?.response?.data?.message || err?.message || "Status update failed",
      );
      return;
    }
    setStatusModal(false);
    setNewStatus("");
    setStatusNote("");
  };

  if (isLoading) return <Spinner full />;
  if (isError || !detail)
    return (
      <ErrorState
        message="Case not found"
        onRetry={() => navigate("/admin/cases")}
      />
    );

  const { caseInfo } = detail;

  return (
    <div
      className="wd-animate"
      style={{ display: "flex", flexDirection: "column", gap: 16 }}
    >
      {/* Breadcrumb */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button
          className="wd-btn wd-btn-ghost wd-btn-sm"
          onClick={() =>
            navigate("/admin/cases", {
              state: {
                highlightedCase: caseInfo.accidentCaseNo,
                listTab: returnListTab,
              },
            })
          }
        >
          <BackIcon /> Back
        </button>
        <span style={{ color: "var(--wd-t3)", fontSize: 13 }}>/</span>
        <span style={{ color: "var(--wd-t3)", fontSize: 13 }}>Cases</span>
        <span style={{ color: "var(--wd-t3)", fontSize: 13 }}>/</span>
        <span
          style={{
            fontFamily: "var(--wd-mono)",
            fontSize: 13,
            color: "var(--wd-blue)",
            fontWeight: 600,
          }}
        >
          {caseInfo.accidentCaseNo}
        </span>
      </div>

      {/* Header card */}
      <div className="wd-card" style={{ padding: 20 }}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <Avatar
              name={caseInfo.customerName || caseInfo.customerPhone || "?"}
              size={48}
            />
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    fontFamily: "var(--wd-mono)",
                    fontSize: 18,
                    fontWeight: 800,
                    color: "var(--wd-blue)",
                  }}
                >
                  {caseInfo.accidentCaseNo}
                </span>
                <PriorityBadge priority={caseInfo.priority} />
                <Badge variant={stageBadge(caseInfo.stage)}>
                  {STAGE_LABELS[caseInfo.stage]}
                </Badge>
                <Badge variant={statusBadge(caseInfo.currentStatus)}>
                  {STATUS_LABELS[caseInfo.currentStatus] ||
                    caseInfo.currentStatus}
                </Badge>
                <SLABadge status={caseInfo.slaStatus} />
                {caseInfo.source && (
                  <Badge variant="gray">
                    {sourceMeta(caseInfo.source).icon}{" "}
                    {sourceMeta(caseInfo.source).label}
                  </Badge>
                )}
              </div>
              <div
                style={{ marginTop: 4, color: "var(--wd-t4)", fontSize: 16 }}
              >
                {caseInfo.customerName} · {caseInfo.customerPhone} ·{" "}
                <span
                  style={{
                    fontFamily: "var(--wd-mono)",
                    color: "var(--wd-blue)",
                    fontSize: 19,
                  }}
                >
                  {caseInfo.vehicleNo}
                </span>
              </div>
              <div
                style={{ marginTop: 2, color: "var(--wd-t3)", fontSize: 12 }}
              >
                📍 {caseInfo.location} · Accident:{" "}
                {formatDate(caseInfo.accidentDate)}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {/* <button className="wd-btn wd-btn-ghost wd-btn-sm"><EditIcon /> Edit</button> */}
            <button
              className="wd-btn wd-btn-ghost wd-btn-sm"
              onClick={() => setShowOverview((v) => !v)}
            >
              {showOverview ? "← Back to tabs" : "🗂 Full overview"}
            </button>
            <button
              className="wd-btn wd-btn-primary wd-btn-sm"
              onClick={() => setStatusModal(true)}
            >
              Update Status
            </button>
          </div>
        </div>

        {/* Stage bar */}
        {!showOverview && (
          <div
            style={{
              marginTop: 16,
              paddingTop: 16,
              borderTop: "1px solid var(--wd-border)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              {Object.entries(STAGE_LABELS).map(([key, label]) => {
                const active = stageIndex(key) <= stageIndex(caseInfo.stage);
                return (
                  <span
                    key={key}
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: ".05em",
                      color: active ? "var(--wd-blue)" : "var(--wd-t3)",
                    }}
                  >
                    {label}
                  </span>
                );
              })}
            </div>
            <StageBar currentStage={caseInfo.stage} />
          </div>
        )}
      </div>

      {/* Quick stats */}
      {!showOverview && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5,1fr)",
            gap: 12,
          }}
        >
          <QuickStat
            icon="💰"
            label="Estimate"
            value={
              caseInfo.workshopEstimateAmount
                ? formatINR(caseInfo.workshopEstimateAmount)
                : null
            }
            pending="Not estimated yet"
          />
          <QuickStat
            icon="🛡️"
            label="Insurance"
            value={caseInfo.insuranceCompany || null}
            pending="Not provided yet"
          />
          <QuickStat
            icon="🔧"
            label="Workshop"
            value={caseInfo.workshopName || null}
            pending="Not assigned yet"
            pendingColor="var(--wd-orange)"
          />
          <QuickStat
            icon="👤"
            label="Surveyor"
            value={caseInfo.surveyorName || null}
            pending="Not assigned yet"
            pendingColor="var(--wd-orange)"
          />
          <QuickStat
            icon="⏰"
            label="SLA Deadline"
            value={
              caseInfo.slaDeadline ? formatDate(caseInfo.slaDeadline) : null
            }
            pending="Not set"
            pendingColor="var(--wd-t3)"
          />
        </div>
      )}

      {/* Progress checklist */}
      {!showOverview && (
        <CaseProgressChecklist caseInfo={caseInfo} detail={detail} />
      )}

      {/* Tabs */}
      {showOverview ? (
        <CaseOverviewSnapshot
          detail={detail}
          onClose={() => setShowOverview(false)}
        />
      ) : (
        <>
          <div
            style={{
              display: "flex",
              borderBottom: "1px solid var(--wd-border)",
            }}
          >
            {DETAIL_TABS.map((t) => (
              <button
                key={t}
                className={`wd-detail-tab ${tab === t ? "active" : ""}`}
                onClick={() => setTab(t)}
              >
                {t}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="wd-animate" key={tab}>
            {tab === "Overview" && <OverviewTab detail={detail} />}
            {tab === "On-Spot Services" && (
              <OnSpotServicesTab
                services={detail.services}
                caseInfo={detail.caseInfo}
                linkedServices={detail.linkedServices}
                accidentDetails={detail.accidentDetails}
                onRefetch={refetch}
              />
            )}
            {tab === "Documents" && (
              <DocumentsTab
                documents={detail.documents}
                caseInfo={detail.caseInfo}
              />
            )}
            {tab === "Insurance" && (
              <InsuranceTab
                detail={detail}
                onSuccess={() => refetch()}
                onGoToSurveys={() => setTab("Surveys")}
              />
            )}
            {tab === "Surveys" && (
              <SurveysTab
                surveys={detail.surveys}
                caseInfo={detail.caseInfo}
                workshop={detail.workshopInfo}
                services={detail.services}
                onGoToWorkshop={() => setTab("Workshop")}
                onGoToServices={() => setTab("On-Spot Services")}
                onRefetch={refetch}
              />
            )}
            {tab === "Workshop" && (
              <WorkshopTab
                workshopInfo={detail.workshopInfo}
                caseInfo={detail.caseInfo}
                surveys={detail.surveys}
                insuranceDetails={detail.insuranceDetails}
                onRefetch={refetch}
                onGoToSurveys={() => setTab("Surveys")}
              />
            )}
            {tab === "Timeline" && (
              <TimelineTab timeline={detail.timeline} caseInfo={detail} />
            )}
            {tab === "Daily Updates" && (
              <DailyUpdateTab
                caseId={caseInfo.accidentCaseNo}
                currentStatus={caseInfo.currentStatus}
                timeline={detail.timeline}
              />
            )}
          </div>
        </>
      )}

      {/* Status modal */}
      <Modal
        open={statusModal}
        onClose={() => setStatusModal(false)}
        title="Update Case Status"
        footer={
          <>
            <button
              className="wd-btn wd-btn-ghost"
              onClick={() => setStatusModal(false)}
            >
              Cancel
            </button>
            <button
              className="wd-btn wd-btn-primary"
              onClick={handleStatusUpdate}
              disabled={!newStatus}
            >
              Update
            </button>
          </>
        }
      >
        <div>
          <label className="wd-form-label">New Status</label>
          <select
            className="wd-input wd-select"
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}
          >
            <option value="">Select status...</option>
            {Object.entries(STATUS_LABELS).map(
              ([k, v]) =>
                (STATUS_ORDER.indexOf(k) >
                  STATUS_ORDER.indexOf(caseInfo.currentStatus) ||
                  ["CASE_CLOSED", "CANCELLED"].includes(k)) && (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ),
            )}
          </select>
          {statusError && (
            <div style={{ marginTop: 6, fontSize: 12, color: "var(--wd-red)" }}>
              {statusError}
            </div>
          )}
        </div>
        <div>
          <label className="wd-form-label">Note</label>
          <textarea
            className="wd-input"
            rows={3}
            style={{ resize: "vertical" }}
            value={statusNote}
            onChange={(e) => setStatusNote(e.target.value)}
            placeholder="Describe this status change..."
          />
        </div>
      </Modal>
    </div>
  );
}
